import React, { useState } from 'react';
import { Button, Input, InputNumber, Spin, Divider, Tag } from 'antd';
import { PrinterOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useJ30C } from './hooks/useJ30C';
import './J30CForm.css';

const J30CForm: React.FC = () => {
  const {
    loading, labelData, printLabels, queried, notFound,
    lookupProduct, doPrint, reset, updateField,
  } = useJ30C();

  const [code1, setCode1] = useState('');
  const [code2, setCode2] = useState('');
  const [copies, setCopies] = useState(1);

  const handleLookup = () => {
    lookupProduct({ product_code1: code1, product_code2: code2 });
  };

  const handlePrint = () => {
    doPrint(copies);
  };

  const handleReset = () => {
    setCode1('');
    setCode2('');
    setCopies(1);
    reset();
  };

  const handlePrintPreview = () => {
    if (!printLabels || printLabels.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const now = new Date().toLocaleString('zh-TW');
    const labelsHtml = printLabels.map((lb, i) => `
      <div class="label" style="border:2px solid #333;padding:12px;margin-bottom:12px;font-family:monospace;page-break-inside:avoid;">
        <div style="font-weight:bold;font-size:14px;margin-bottom:6px;">
          ${lb.product_code1} ${lb.product_code2}
        </div>
        <div style="white-space:pre-wrap;margin-bottom:6px;">${lb.description}</div>
        <div>Processing: ${lb.processing}</div>
        <div>Width: ${lb.width_formatted} / ${lb.width_cm_fmt}</div>
        <div>Weight: ${lb.weight_gsm} / ${lb.weight_oz_yd}</div>
        <div style="text-align:right;font-size:10px;color:#999;">Label ${i + 1}/${printLabels.length}</div>
      </div>
    `).join('');

    win.document.write(`<!DOCTYPE html><html><head><title>A4 Label Print</title>
<style>
  body { font-family: 'Microsoft JhengHei', sans-serif; margin: 20px; font-size: 12px; }
  h3 { text-align: center; }
  .meta { text-align: center; font-size: 11px; color: #555; margin-bottom: 16px; }
  @media print { body { margin: 10mm; } }
</style></head><body>
<h3>A4 Label Print - pwj30cf1</h3>
<div class="meta">Print time: ${now}</div>
${labelsHtml}
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <Spin spinning={loading}>
      <div className="j30c-container">
        <h2 className="j30c-title">A4 Label Print (pwj30cf1)</h2>

        {/* Lookup Panel */}
        <div className="j30c-lookup-panel">
          <div className="j30c-lookup-row">
            <span className="j30c-lookup-label">Product Code</span>
            <Input
              value={code1} onChange={e => setCode1(e.target.value.toUpperCase())}
              placeholder="Front (6 chars)" style={{ width: 140 }} maxLength={6}
              onPressEnter={handleLookup}
            />
            <Input
              value={code2} onChange={e => setCode2(e.target.value.toUpperCase())}
              placeholder="Rear (6 chars)" style={{ width: 140 }} maxLength={6}
              onPressEnter={handleLookup}
            />
          </div>
          <div className="j30c-lookup-actions">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleLookup}
              disabled={!code1.trim()}>
              Lookup
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>Clear</Button>
          </div>
        </div>

        {/* Spec Panel */}
        {queried && labelData && (
          <>
            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
              Spec Data {labelData.is_english && <Tag color="blue">English</Tag>}
            </Divider>
            <div className="j30c-spec-panel">
              <div className="j30c-spec-row">
                <span className="j30c-spec-label">Description</span>
                <div className="j30c-desc-box">{labelData.description}</div>
              </div>
              <div className="j30c-spec-row">
                <span className="j30c-spec-label">Processing</span>
                <span className="j30c-spec-value">{labelData.processing}</span>
              </div>
              <div className="j30c-spec-row">
                <span className="j30c-spec-label">Width (inch)</span>
                <InputNumber value={labelData.width_inch} style={{ width: 100 }}
                  onChange={v => updateField('width_inch', v ?? 0)} step={0.1} />
                <span className="j30c-spec-value">{labelData.width_cm} cm</span>
              </div>
              <div className="j30c-spec-row">
                <span className="j30c-spec-label">Width 2 (inch)</span>
                <InputNumber value={labelData.width2_inch} style={{ width: 100 }}
                  onChange={v => updateField('width2_inch', v ?? 0)} step={0.1} />
                <span className="j30c-spec-value">{labelData.width2_cm} cm</span>
              </div>
              <div className="j30c-spec-row">
                <span className="j30c-spec-label">Weight (g/m2)</span>
                <InputNumber value={labelData.weight_gsm} style={{ width: 100 }}
                  onChange={v => updateField('weight_gsm', v ?? 0)} step={0.1} />
                <span className="j30c-spec-value">{labelData.weight_oz_yd} oz/yd2</span>
              </div>

              {/* Print Controls */}
              <Divider />
              <div className="j30c-print-panel">
                <div className="j30c-print-actions">
                  <span className="j30c-spec-label">Copies</span>
                  <InputNumber min={1} max={99} value={copies}
                    onChange={v => setCopies(v ?? 1)} style={{ width: 80 }} />
                  <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                    Generate Labels
                  </Button>
                  {printLabels && printLabels.length > 0 && (
                    <Button icon={<PrinterOutlined />} onClick={handlePrintPreview}>
                      Print Preview
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Label Preview */}
            {printLabels && printLabels.length > 0 && (
              <>
                <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
                  Label Preview ({printLabels.length} labels)
                </Divider>
                <div className="j30c-label-preview">
                  {printLabels.slice(0, 5).map((lb, i) => (
                    <div key={i} className="j30c-label-card">
                      <div className="label-row">
                        <strong>{lb.product_code1} {lb.product_code2}</strong>
                        <span>#{i + 1}</span>
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', marginBottom: 4 }}>
                        {lb.description}
                      </div>
                      <div className="label-row">
                        <span>Processing: {lb.processing}</span>
                      </div>
                      <div className="label-row">
                        <span>Width: {lb.width_formatted}</span>
                        <span>{lb.width_cm_fmt}</span>
                      </div>
                      <div className="label-row">
                        <span>Weight: {lb.weight_gsm}</span>
                        <span>{lb.weight_oz_yd}</span>
                      </div>
                    </div>
                  ))}
                  {printLabels.length > 5 && (
                    <div style={{ textAlign: 'center', color: '#999', padding: 8 }}>
                      ... and {printLabels.length - 5} more labels
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {queried && notFound && (
          <div className="j30c-no-data">Query result: product not found</div>
        )}
      </div>
    </Spin>
  );
};

export default J30CForm;
